import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Search, X, Award } from 'lucide-react';
import '../styles/MasterForms.css';
import apiClient from '../services/api';

const Designations = () => {
  // ✅ Get session data from localStorage
  const getSessionData = () => {
    try {
      const sessionStr = localStorage.getItem('ltms_session');
      if (!sessionStr) return null;
      
      const sessionData = JSON.parse(sessionStr);
      return sessionData?.session || sessionData;
    } catch (error) {
      console.error('Error parsing session data:', error);
      return null;
    }
  };

  const session = getSessionData();
  const companyId = session?.companyid;
  const yearId = session?.yearid;

  // ✅ ALL HOOKS FIRST (before any conditional returns)
  const [designations, setDesignations] = useState([]);
  const [filteredDesignations, setFilteredDesignations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  
  const [formData, setFormData] = useState({
    designationid: null,
    companyid: companyId || null,  // ✅ From session
    designationcode: '', // Auto-generated
    designationname: '',
    designationlevel: '',
    isactive: true
  });
  
  const [errors, setErrors] = useState({});
  const [submitLoading, setSubmitLoading] = useState(false);

  useEffect(() => {
    if (!companyId || !yearId) {
      setLoading(false);
      return;
    }
    fetchDesignations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, yearId]);

  useEffect(() => {
    filterDesignations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, designations]);

  const fetchDesignations = async () => {
    setLoading(true);
    try {
      // ✅ Use apiClient with session companyId
      const response = await apiClient.get('/Designations.php', {
        params: { companyid: companyId }
      });
      
      if (response.data.success) {
        setDesignations(response.data.data || []);
      } else {
        alert('Failed to fetch designations: ' + response.data.message);
      }
    } catch (error) {
      console.error('Error fetching designations:', error);
      if (error.response?.status === 401) {
        alert('Session expired. Please login again.');
      } else {
        alert('Error fetching designations. Please check if backend is running.');
      }
    } finally {
      setLoading(false);
    }
  };

  const filterDesignations = () => {
    if (!searchTerm.trim()) {
      setFilteredDesignations(designations);
    } else {
      const filtered = designations.filter(desig =>
        desig.designationname.toLowerCase().includes(searchTerm.toLowerCase()) ||
        desig.designationcode?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredDesignations(filtered);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.designationname.trim()) {
      newErrors.designationname = 'Designation name is required';
    }
    
    if (formData.designationlevel && (isNaN(formData.designationlevel) || formData.designationlevel < 1 || formData.designationlevel > 100)) {
      newErrors.designationlevel = 'Level must be between 1 and 100';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setSubmitLoading(true);
    
    try {
      // ✅ Use apiClient
      const method = editMode ? 'put' : 'post';
      const response = await apiClient[method]('/Designations.php', {
        ...formData,
        companyid: companyId,  // ✅ Use session companyId
        isactive: formData.isactive ? 1 : 0
      });
      
      const data = response.data;
      
      if (data.success) {
        if (!editMode && data.data && data.data.designationcode) {
          alert(`Designation created successfully!\nDesignation Code: ${data.data.designationcode}`);
        } else {
          alert(editMode ? 'Designation updated successfully!' : 'Designation created successfully!');
        }
        setShowModal(false);
        resetForm();
        fetchDesignations();
      } else {
        alert('Error: ' + data.message);
      }
    } catch (error) {
      console.error('Error saving designation:', error);
      if (error.response?.status === 401) {
        alert('Session expired. Please login again.');
      } else {
        alert('Error saving designation. Please try again.');
      }
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleEdit = (designation) => {
    setFormData({
      designationid: designation.designationid,
      companyid: designation.companyid,
      designationcode: designation.designationcode,
      designationname: designation.designationname,
      designationlevel: designation.designationlevel || '',
      isactive: designation.isactive === 1
    });
    setEditMode(true);
    setShowModal(true);
    setErrors({});
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this designation?')) {
      return;
    }
    
    try {
      // ✅ Use apiClient
      const response = await apiClient.delete('/Designations.php', {
        data: { designationid: id }
      });
      
      const data = response.data;
      
      if (data.success) {
        alert('Designation deleted successfully!');
        fetchDesignations();
      } else {
        alert('Error: ' + data.message);
      }
    } catch (error) {
      console.error('Error deleting designation:', error);
      if (error.response?.status === 401) {
        alert('Session expired. Please login again.');
      } else {
        alert('Error deleting designation. Please try again.');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      designationid: null,
      companyid: companyId || null,  // ✅ From session
      designationcode: '',
      designationname: '',
      designationlevel: '',
      isactive: true
    });
    setEditMode(false);
    setErrors({});
  };

  const openAddModal = () => {
    resetForm();
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    resetForm();
  };

  const getLevelBadge = (level) => {
    if (!level) return '-';
    if (level <= 3) return <span className="type-badge" style={{background: '#fee2e2', color: '#991b1b'}}>Level {level} - Executive</span>;
    if (level <= 6) return <span className="type-badge" style={{background: '#dbeafe', color: '#1e40af'}}>Level {level} - Management</span>;
    return <span className="type-badge" style={{background: '#fef3c7', color: '#92400e'}}>Level {level} - Staff</span>;
  };

  // ✅ Check session context - AFTER all hooks
  if (!companyId || !yearId) {
    return (
      <div className="master-container">
        <div className="empty-state">
          <h3>No Session Context</h3>
          <p>Please select a company and financial year from the dashboard.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="master-container">
      <div className="master-header">
        <div className="header-left">
          <h2>Designations Master</h2>
          <p className="subtitle">Manage job titles and positions</p>
        </div>
        <button className="btn-primary" onClick={openAddModal}>
          <Plus size={20} />
          Add Designation
        </button>
      </div>

      <div className="master-toolbar">
        <div className="search-box">
          <Search size={20} />
          <input
            type="text"
            placeholder="Search by designation name or code..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button className="btn-clear" onClick={() => setSearchTerm('')}>
              <X size={16} />
            </button>
          )}
        </div>
        <div className="toolbar-info">
          Total: {filteredDesignations.length} {filteredDesignations.length === 1 ? 'designation' : 'designations'}
        </div>
      </div>

      {loading ? (
        <div className="loading-state">Loading designations...</div>
      ) : filteredDesignations.length === 0 ? (
        <div className="empty-state">
          <p>No designations found</p>
          {searchTerm && <button onClick={() => setSearchTerm('')}>Clear search</button>}
        </div>
      ) : (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Code</th>
                <th>Designation Name</th>
                <th>Level/Hierarchy</th>
                <th>Status</th>
                <th>Created At</th>
                <th className="actions-column">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredDesignations.map((designation) => (
                <tr key={designation.designationid}>
                  <td className="font-mono">{designation.designationcode}</td>
                  <td className="font-semibold">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Award size={16} color="#667eea" />
                      {designation.designationname}
                    </div>
                  </td>
                  <td>{getLevelBadge(designation.designationlevel)}</td>
                  <td>
                    <span className={`status-badge ${designation.isactive ? 'active' : 'inactive'}`}>
                      {designation.isactive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>{new Date(designation.createdat).toLocaleDateString()}</td>
                  <td className="actions-column">
                    <button
                      className="btn-icon btn-edit"
                      onClick={() => handleEdit(designation)}
                      title="Edit"
                    >
                      <Edit2 size={16} />
                    </button>
                    <button
                      className="btn-icon btn-delete"
                      onClick={() => handleDelete(designation.designationid)}
                      title="Delete"
                    >
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editMode ? 'Edit Designation' : 'Add New Designation'}</h3>
              <button className="btn-close" onClick={closeModal}>
                <X size={24} />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="modal-form">
              {!editMode && (
                <div style={{
                  background: 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)',
                  border: '2px solid #3b82f6',
                  borderRadius: '10px',
                  padding: '12px 16px',
                  marginBottom: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px'
                }}>
                  <span style={{ fontSize: '20px' }}>ℹ️</span>
                  <span style={{ fontSize: '14px', fontWeight: '600', color: '#1e40af' }}>
                    Designation code will be auto-generated (e.g., DESG001, DESG002, etc.)
                  </span>
                </div>
              )}
              
              {editMode && formData.designationcode && (
                <div className="form-group">
                  <label>Designation Code (Auto-generated)</label>
                  <input
                    type="text"
                    value={formData.designationcode}
                    disabled
                    style={{ background: '#f3f4f6', cursor: 'not-allowed' }}
                  />
                </div>
              )}

              <div className="form-group">
                <label htmlFor="designationname">
                  Designation Name <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="designationname"
                  value={formData.designationname}
                  onChange={(e) => setFormData({ ...formData, designationname: e.target.value })}
                  maxLength="100"
                  placeholder="e.g., Manager, Senior Executive, Officer"
                  className={errors.designationname ? 'error' : ''}
                />
                {errors.designationname && (
                  <span className="error-message">{errors.designationname}</span>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="designationlevel">Designation Level</label>
                <input
                  type="number"
                  id="designationlevel"
                  value={formData.designationlevel}
                  onChange={(e) => setFormData({ ...formData, designationlevel: e.target.value })}
                  min="1"
                  max="100"
                  placeholder="1-100 (1=Highest, 100=Lowest)"
                  className={errors.designationlevel ? 'error' : ''}
                />
                {errors.designationlevel && (
                  <span className="error-message">{errors.designationlevel}</span>
                )}
                <small style={{ color: '#6b7280', fontSize: '12px', display: 'block', marginTop: '4px' }}>
                  Optional: Define hierarchy (1-3: Executive, 4-6: Management, 7+: Staff)
                </small>
              </div>

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.isactive}
                    onChange={(e) => setFormData({ ...formData, isactive: e.target.checked })}
                  />
                  <span>Active</span>
                </label>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={closeModal}
                  disabled={submitLoading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={submitLoading}
                >
                  {submitLoading ? 'Saving...' : (editMode ? 'Update' : 'Create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Designations;